import type {
  ConnectionState,
  Participant,
  ParticipantPropertiesPayload,
  event_peer_video_state_change,
} from "@zoom/videosdk";
import { logger } from "../../../../datadog";
import { RemoteMediaDelegator } from "../../../core/room/participant/media/MediaDelegator";
import { MediaKind } from "../../../core/room/participant/media/types";
import { RemoteParticipantEventHandler } from "../../../core/room/participant/types";
import { handleUnhandledEventHandlerException } from "../../../core/utils";
import { VideoClient } from "../../types";
import { EVENT_CONFIG_MAP, SupportedRemoteParticipantEvent } from "./events";
import { ZoomRemoteAudioDelegator } from "./media/RemoteAudioDelegator";
import { ZoomRemoteDataDelegator } from "./media/RemoteDataDelegator";
import { ZoomRemoteScreenShareDelegator } from "./media/RemoteScreenShareDelegator";
import { ZoomRemoteVideoDelegator } from "./media/RemoteVideoDelegator";
import { PeerShareStateChangePayload, ZoomEventTrigger } from "./predicates";

type VideoStateChangePayload = Parameters<
  typeof event_peer_video_state_change
>[0];

export function isEventSupported(
  event: string,
): event is SupportedRemoteParticipantEvent {
  return event in EVENT_CONFIG_MAP;
}

/**
 * Union of all possible Zoom event mapping configurations.
 * Each variant corresponds to a specific Zoom SDK event with its properly-typed predicate.
 */
type ZoomEventMapping =
  | ZoomEventTrigger<"user-updated">
  | ZoomEventTrigger<"peer-video-state-change">
  | ZoomEventTrigger<"command-channel-status">
  | ZoomEventTrigger<"peer-share-state-change">;

export function getZoomEventMappings<K extends SupportedRemoteParticipantEvent>(
  standardEvent: K,
  mediaKind: MediaKind,
): readonly ZoomEventMapping[] | undefined {
  const mappingsByMediaKind = EVENT_CONFIG_MAP[standardEvent];
  if (!mappingsByMediaKind) {
    logger.warn(
      `[getEventHandlerConfig] No configuration found for standardEvent: ${standardEvent}`,
    );
    return undefined;
  }

  const eventMappings = mappingsByMediaKind[mediaKind];
  if (!eventMappings) {
    logger.warn(
      `[getEventHandlerConfig] No Zoom event mappings found for mediaKind: ${mediaKind} with standardEvent: ${standardEvent}`,
    );
    return undefined;
  }

  return eventMappings;
}

type CreateZoomHandlerFromEventMapping<
  K extends SupportedRemoteParticipantEvent,
> = {
  eventMapping: ZoomEventMapping;
  videoClient: VideoClient;
  participant: Participant;
  handler: RemoteParticipantEventHandler<K>;
  mediaKind: MediaKind;
};

// Create handlers directly based on Zoom event type using EventHandlerConfig
// EventHandlerConfig is a discriminated union type and lets this return a type-safe handler
export function createZoomHandlerFromEventMapping<
  K extends SupportedRemoteParticipantEvent,
>({
  eventMapping,
  videoClient,
  participant,
  handler,
  mediaKind,
}: CreateZoomHandlerFromEventMapping<K>) {
  const { zoomEvent, predicate } = eventMapping;
  switch (zoomEvent) {
    case "user-updated":
      return (payload: ParticipantPropertiesPayload[]) => {
        if (predicate(payload, participant.userId)) {
          const delegator = transformZoomResponseToDelegator(
            videoClient,
            participant,
            mediaKind,
          );
          const result = handler(delegator);
          handleUnhandledEventHandlerException(result, "Zoom: user-updated");
        }
      };
    case "peer-video-state-change":
      return (payload: VideoStateChangePayload) => {
        if (predicate(payload, participant.userId)) {
          const delegator = transformZoomResponseToDelegator(
            videoClient,
            participant,
            mediaKind,
          );
          const result = handler(delegator);
          handleUnhandledEventHandlerException(
            result,
            "Zoom: peer-video-state-change",
          );
        }
      };
    case "command-channel-status":
      return (payload: ConnectionState) => {
        if (predicate(payload)) {
          const delegator = transformZoomResponseToDelegator(
            videoClient,
            participant,
            mediaKind,
          );
          const result = handler(delegator);
          handleUnhandledEventHandlerException(
            result,
            "Zoom: command-channel-status",
          );
        }
      };

    case "peer-share-state-change":
      return (payload: PeerShareStateChangePayload) => {
        logger.info("Zoom active-share-change event received", {
          userId: payload.userId,
          action: payload.action,
          participantUserId: participant.userId,
          participantIdentity: participant.userIdentity,
          activeShareUserId: videoClient
            .getMediaStream()
            .getActiveShareUserId(),
        });

        if (predicate(payload, participant.userId)) {
          // Get the participant who is sharing from the payload
          const sharingParticipant = videoClient.getUser(payload.userId);
          if (sharingParticipant) {
            logger.info("Remote participant started screen share", {
              sharingUserId: sharingParticipant.userId,
              sharingUserName: sharingParticipant.displayName,
            });
            const delegator = new ZoomRemoteScreenShareDelegator(
              sharingParticipant,
              videoClient.getMediaStream(),
            );
            const result = handler(delegator);
            handleUnhandledEventHandlerException(
              result,
              "Zoom: active-share-change",
            );
          } else {
            logger.warn(
              "active-share-change: Could not get sharing participant",
              {
                userId: payload.userId,
              },
            );
          }
        } else {
          logger.debug("active-share-change: predicate returned false");
        }
      };

    default: {
      const exhaustiveCheck: never = zoomEvent;
      return exhaustiveCheck;
    }
  }
}

export function transformZoomResponseToDelegator(
  videoClient: VideoClient,
  participant: Participant,
  mediaKind: MediaKind,
): RemoteMediaDelegator {
  // the participant data could be stale, get latest if possible
  const latestParticipant =
    videoClient.getUser(participant.userId) ?? participant;
  switch (mediaKind) {
    case "audio":
      return new ZoomRemoteAudioDelegator(latestParticipant);
    case "video":
      return new ZoomRemoteVideoDelegator(
        latestParticipant,
        videoClient.getMediaStream(),
      );
    case "data":
      return new ZoomRemoteDataDelegator(latestParticipant);
    default: {
      const _exhaustive: never = mediaKind;
      return _exhaustive;
    }
  }
}
